import { Download, LoaderCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import type { UpdateInfo, UpdateInstallState } from '@/hooks/useUpdateCheck';

interface UpdateModalProps {
  open: boolean;
  latestVersion: UpdateInfo | null;
  currentVersion: { versionCode: number; versionName: string } | null;
  installState: UpdateInstallState;
  onDismiss: () => void;
  onPrimaryAction: () => void | Promise<void>;
}

function formatBytes(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 MB';
  }

  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getProgressValue(installState: UpdateInstallState) {
  if (installState.kind !== 'downloading') {
    return 0;
  }

  if (installState.totalBytes > 0) {
    return Math.max(0, Math.min(100, Math.round((installState.bytesReceived / installState.totalBytes) * 100)));
  }

  return Math.max(0, Math.min(100, Math.round(installState.progress * 100)));
}

function getModalTitle(installState: UpdateInstallState) {
  switch (installState.kind) {
    case 'downloading':
      return 'Downloading Update';
    case 'awaitingPermission':
      return 'Allow App Installs';
    case 'installing':
      return 'Opening Installer';
    case 'error':
      return installState.phase === 'download' ? 'Download Failed' : 'Install Failed';
    default:
      return 'Update Available';
  }
}

function getPrimaryLabel(installState: UpdateInstallState) {
  switch (installState.kind) {
    case 'downloading':
      return 'Downloading...';
    case 'awaitingPermission':
      return 'Install';
    case 'installing':
      return 'Installing...';
    case 'error':
      return installState.phase === 'download' ? 'Retry Download' : 'Retry Install';
    default:
      return 'Install';
  }
}

function getStatusCopy(installState: UpdateInstallState) {
  switch (installState.kind) {
    case 'downloading':
      return 'The update is downloading inside the app.';
    case 'awaitingPermission':
      return 'Allow installs from this app in Android settings, then return here to continue.';
    case 'installing':
      return 'Android installer is opening now.';
    case 'error':
      return installState.message;
    default:
      return 'A new version of Ping is available.';
  }
}

function getProgressCopy(installState: UpdateInstallState, latestVersion: UpdateInfo | null) {
  if (installState.kind !== 'downloading') {
    return null;
  }

  const totalBytes = installState.totalBytes || latestVersion?.fileSize || 0;
  if (totalBytes > 0) {
    const percent = Math.max(0, Math.min(100, Math.round((installState.bytesReceived / totalBytes) * 100)));
    return `${formatBytes(installState.bytesReceived)} / ${formatBytes(totalBytes)} (${percent}%)`;
  }

  return `${formatBytes(installState.bytesReceived)} downloaded`;
}

export function UpdateModal({
  open,
  latestVersion,
  currentVersion,
  installState,
  onDismiss,
  onPrimaryAction,
}: UpdateModalProps) {
  const isBusy = installState.kind === 'downloading' || installState.kind === 'installing';
  const progressCopy = getProgressCopy(installState, latestVersion);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onDismiss()}>
      <DialogContent className="max-w-sm w-[calc(100%-2rem)]">
        <DialogHeader className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center">
            <Download className="w-8 h-8 text-white" />
          </div>
          <DialogTitle className="text-xl">{getModalTitle(installState)}</DialogTitle>
          <DialogDescription className="space-y-2">
            <p>{getStatusCopy(installState)}</p>
            {currentVersion && latestVersion && (
              <p className="text-sm">
                <span className="text-muted-foreground">Current: </span>
                <span className="font-medium">{currentVersion.versionName}</span>
                <span className="text-muted-foreground mx-2">→</span>
                <span className="text-muted-foreground">New: </span>
                <span className="font-medium text-primary">{latestVersion.versionName}</span>
              </p>
            )}
          </DialogDescription>
        </DialogHeader>

        {latestVersion?.releaseNotes && (
          <div className="rounded-lg bg-muted/50 p-3 text-sm">
            <p className="font-medium text-foreground mb-1">What's new:</p>
            <p className="text-muted-foreground whitespace-pre-wrap">
              {latestVersion.releaseNotes}
            </p>
          </div>
        )}

        {installState.kind === 'downloading' && (
          <div className="space-y-2 rounded-lg border border-border/60 bg-background/60 p-3">
            <Progress value={getProgressValue(installState)} />
            <p className="text-xs text-muted-foreground text-center">{progressCopy}</p>
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            onClick={() => void onPrimaryAction()}
            className="w-full bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 hover:from-violet-500 hover:via-fuchsia-500 hover:to-pink-500"
            size="lg"
            disabled={!latestVersion || isBusy}
          >
            {isBusy ? (
              <LoaderCircle className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <Download className="w-5 h-5 mr-2" />
            )}
            {getPrimaryLabel(installState)}
          </Button>
          <Button
            onClick={onDismiss}
            variant="ghost"
            className="w-full"
          >
            <X className="w-4 h-4 mr-2" />
            Later
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
